import type { Degree } from "@pdfking/math"
import { Total } from "@pdfking/math"
import { ZERO_DISTANCE } from "@pdfking/math/constants"
import type { PageShape } from "@pdfking/renderer-api"
import { GroupMatrix, PlotFunction } from "@pdfking/renderer-api"
import { GrowContentKind } from "@pdfking/templates"

import { PALETTE, POSTER_TITLE } from "#constants"

import { DrawBackpackGrid } from "../pattern/DrawBackpackGrid"
import { DrawPoster } from "../poster/DrawPoster"
import { CreateRandom } from "../random/CreateRandom"
import { PosterWord } from "../text/PosterWord"
import type { Composition } from "./Composition"
import { ComposePieces, Ground, Turned } from "./Composition"
import { PosterRows } from "./PosterRows"

/** A depliant — a fold, a folder, a sheet — made of its pieces: the pages in
 * a row along the opening are one poster across the row, each a panel of it,
 * as facing pages are — a gusset between two of them a panel never drawn —,
 * the page numbered 1 carrying SAMPLE, its text within the security, a lone
 * page its own poster upright;
 * a row of posters laid upside down is
 * turned as one. A spine is the primary alone; a flap wears the pattern on
 * the outside. The inside is the pattern end to end, the pieces mirrored
 * along the opening. Every ground runs on into the bleed where its piece is
 * at the page's edge. */
export const DepliantContent = (c: Composition): PageShape[] => {
  const { is_front, measure, opening_top, seed, short } = c
  const rows = PosterRows(c)
  return [
    ...Ground(c),
    ...ComposePieces(
      c,
      {
        [GrowContentKind.FLAP]: ({ box, index, reach }) =>
          is_front
            ? DrawBackpackGrid({
                bleed: reach,
                box,
                random: CreateRandom(seed, "flap", index),
                short,
              })
            : [],
        [GrowContentKind.PAGE]: ({ index, reach, security, turned }) => {
          const row = rows.get(index)!
          const random = CreateRandom(seed, "poster row", is_front, row.index)
          const word = row.sample
            ? POSTER_TITLE
            : PosterWord(seed, row.index + (is_front ? 0 : 100))
          const { span } = row
          // A lone page is its own poster, upright whichever way it opens
          const poster: PageShape[] =
            opening_top && row.widths.length > 1
              ? // The row runs down the page: the poster on its side, its panels
                // from the top of the row down
                [
                  {
                    enum: PlotFunction.PLOT_GROUP,
                    matrix: GroupMatrix({
                      rotation: -90 as Degree,
                      translate: { x: span.x, y: Total(span.y, span.height) },
                    }),
                    shapes: DrawPoster({
                      bleed: {
                        bottom: reach.left,
                        left: reach.top,
                        right: reach.bottom,
                        top: reach.right,
                      },
                      box: {
                        height: span.width,
                        width: span.height,
                        x: ZERO_DISTANCE,
                        y: ZERO_DISTANCE,
                      },
                      measure,
                      page: row.panel.get(index)!,
                      panels: row.widths,
                      random,
                      security: {
                        bottom: security.left,
                        left: security.top,
                        right: security.bottom,
                        top: security.right,
                      },
                      word,
                      word_panel: row.word_panel,
                    }),
                  },
                ]
              : DrawPoster({
                  bleed: reach,
                  box: span,
                  measure,
                  page: row.panel.get(index)!,
                  panels: row.widths,
                  random,
                  security,
                  word,
                  word_panel: row.word_panel,
                })
          return turned ? [Turned(span, poster)] : poster
        },
        [GrowContentKind.SPINE]: ({ box }) => [
          { enum: PlotFunction.PLOT_RECT, fill_color: PALETTE.primary, position: box },
        ],
      },
      true
    ),
  ]
}
